import type { CourseVigencyI, ExpirationRuleI } from 'types';

/**
 * Días por unidad, deliberadamente la misma tabla para el ciclo y para el
 * plazo: así "cada mes con plazo de 1 mes" se acepta y "cada mes con plazo de
 * 5 semanas" se rechaza. El mes vale 28 —su duración mínima— para que un plazo
 * de 30 días sobre un ciclo mensual no se acepte y luego se desborde en
 * febrero.
 */
export const CYCLE_DAYS: Record<string, number> = { day: 1, week: 7, month: 28, year: 365 };

const UNIT_LABEL: Record<string, [string, string]> = {
  day: ['día', 'días'],
  week: ['semana', 'semanas'],
  month: ['mes', 'meses'],
  year: ['año', 'años'],
};

const WEEKDAY_LABEL = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAYS_BEFORE_MONTH = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

const isLeapYear = (year: number): boolean => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const parseCalendarDate = (value: string): [number, number, number] | null => {
  const match = CALENDAR_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1) return null;

  const daysInMonth =
    month === 2 && isLeapYear(year) ? 29 : [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return day <= daysInMonth ? [year, month, day] : null;
};

/**
 * Comprueba que una cadena sea exactamente una fecha calendario YYYY-MM-DD.
 * La validación usa componentes numéricos y no depende de la zona horaria.
 *
 * @param {string} value - Fecha que se desea validar
 * @returns {boolean} `true` cuando la fecha existe en el calendario
 */
export const isValidCalendarDate = (value: string): boolean => parseCalendarDate(value) !== null;

const calendarDateToDayNumber = (value: string): number => {
  const [year, month, day] = parseCalendarDate(value) as [number, number, number];
  const previousYear = year - 1;
  const daysBeforeYear =
    365 * previousYear + Math.floor(previousYear / 4) - Math.floor(previousYear / 100) + Math.floor(previousYear / 400);
  const leapDay = month > 2 && isLeapYear(year) ? 1 : 0;

  return daysBeforeYear + DAYS_BEFORE_MONTH[month - 1] + leapDay + day - 1;
};

const plural = (amount: number, unit: string): string => {
  const [one, many] = UNIT_LABEL[unit] || ['', ''];
  return amount === 1 ? one : many;
};

/**
 * Convierte una cantidad y una unidad a días.
 *
 * Si la unidad no existe en la tabla, devuelve cero para que la validación
 * superior pueda rechazarla sin inventar equivalencias.
 *
 * @param {number} amount - Cantidad de unidades a convertir
 * @param {string} unit - Unidad del ciclo o del plazo
 * @returns {number} Días equivalentes o cero si la unidad es desconocida
 */
export const toDays = (amount: number, unit: string): number => amount * (CYCLE_DAYS[unit] || 0);

/**
 * Calcula la duración mínima del ciclo en días.
 *
 * Para reglas no repetibles o incompletas devuelve `Infinity`, que en la
 * comparación de plazos significa "no hay un ciclo finito que limite".
 *
 * @param {ExpirationRuleI | null} rule - Regla de expiración configurada
 * @returns {number} Duración mínima del ciclo en días
 */
export const cycleDays = (rule: ExpirationRuleI | null): number => {
  const normalized = rule || { type: 'none' as const };

  switch (normalized.type) {
    case 'interval':
      return toDays(normalized.interval || 0, normalized.unit || '');
    case 'weekday':
      return 7;
    case 'monthday':
      return CYCLE_DAYS.month;
    case 'range': {
      if (!normalized.start || !normalized.end) return Infinity;
      if (!isValidCalendarDate(normalized.start) || !isValidCalendarDate(normalized.end)) return Infinity;
      const days = calendarDateToDayNumber(normalized.end) - calendarDateToDayNumber(normalized.start);
      return days + 1;
    }
    default:
      return Infinity;
  }
};

/**
 * Valida que el ciclo de vigencia y el plazo no se contradigan.
 *
 * Replica en frontend las reglas del backend para evitar viajes innecesarios,
 * pero la autoridad final sigue estando del lado del servidor.
 *
 * @param {CourseVigencyI} vigency - Configuración completa de vigencia
 * @returns {string[]} Lista de errores en español; vacía cuando es válida
 */
export const validateVigency = ({ expirationRule, dueAmount, dueUnit }: CourseVigencyI): string[] => {
  const rule = expirationRule || { type: 'none' as const };
  const errors: string[] = [];

  if (dueUnit && (!dueAmount || dueAmount <= 0)) errors.push('El plazo debe ser mayor a cero.');
  if (dueAmount && !dueUnit) errors.push('Indica la unidad del plazo.');

  switch (rule.type) {
    case 'interval':
      if (!rule.interval || rule.interval < 1) errors.push('El intervalo debe ser al menos 1.');
      if (!rule.unit || !CYCLE_DAYS[rule.unit]) errors.push('Unidad del ciclo inválida.');
      if (!rule.start) errors.push('Indica la fecha de inicio del primer ciclo.');
      else if (!isValidCalendarDate(rule.start))
        errors.push('La fecha de inicio del primer ciclo no es válida (YYYY-MM-DD).');
      break;
    case 'weekday':
      if (!rule.anchor || rule.anchor < 1 || rule.anchor > 7) errors.push('Día de la semana inválido.');
      break;
    case 'monthday':
      if (!rule.anchor || rule.anchor < 1 || rule.anchor > 31) errors.push('Día del mes inválido.');
      break;
    case 'range':
      if (!rule.start || !rule.end) errors.push('Indica la fecha de inicio y la fecha final.');
      else {
        const startIsValid = isValidCalendarDate(rule.start);
        const endIsValid = isValidCalendarDate(rule.end);

        if (!startIsValid) errors.push('La fecha de inicio no es válida (YYYY-MM-DD).');
        if (!endIsValid) errors.push('La fecha final no es válida (YYYY-MM-DD).');
        if (startIsValid && endIsValid && calendarDateToDayNumber(rule.end) <= calendarDateToDayNumber(rule.start)) {
          errors.push('La fecha final debe ser posterior a la inicial.');
        }
      }
      break;
    case 'none':
      break;
    default:
      errors.push('Tipo de expiración inválido.');
  }

  // Comparar plazo contra ciclo solo tiene sentido cuando la regla base ya es
  // válida. Si la regla ya trae errores, agregar además un error de
  // plazo-vs-ciclo mete ruido y oculta la causa real que el usuario debe
  // corregir primero.
  if (errors.length === 0 && dueAmount && dueUnit && toDays(dueAmount, dueUnit) > cycleDays(rule)) {
    errors.push(
      rule.type === 'range'
        ? 'El plazo no puede exceder el rango configurado.'
        : 'El plazo no puede ser mayor al ciclo de expiración.'
    );
  }

  return errors;
};

// El resumen se pinta mientras el usuario todavía está capturando: una regla a
// medias no debe imprimir `undefined` ni inventar un día que nadie eligió.
const INCOMPLETE_CYCLE = 'Ciclo incompleto';

const describeCycle = (rule: ExpirationRuleI | null): string => {
  const normalized = rule || { type: 'none' as const };

  switch (normalized.type) {
    case 'interval': {
      const amount = normalized.interval || 0;
      if (amount < 1 || !normalized.unit || !UNIT_LABEL[normalized.unit]) return INCOMPLETE_CYCLE;
      const unit = plural(amount, normalized.unit);
      return amount === 1 ? `Cada ${unit}` : `Cada ${amount} ${unit}`;
    }
    case 'weekday': {
      const anchor = normalized.anchor || 0;
      if (anchor < 1 || anchor > 7) return INCOMPLETE_CYCLE;
      return `Cada ${WEEKDAY_LABEL[anchor - 1]}`;
    }
    case 'monthday': {
      const anchor = normalized.anchor || 0;
      if (anchor < 1 || anchor > 31) return INCOMPLETE_CYCLE;
      return `El día ${anchor} de cada mes`;
    }
    case 'range':
      if (!normalized.start || !normalized.end) return INCOMPLETE_CYCLE;
      return `Del ${normalized.start} al ${normalized.end}`;
    default:
      return 'Sin ciclo';
  }
};

/**
 * Resume la vigencia en una frase reutilizable para UI administrativa y
 * editor, de modo que ambos muestren exactamente el mismo texto.
 *
 * @param {CourseVigencyI} vigency - Configuración completa de vigencia
 * @returns {string} Descripción legible del ciclo y su plazo
 */
export const describeRule = ({ expirationRule, dueAmount, dueUnit }: CourseVigencyI): string => {
  const cycle = describeCycle(expirationRule);
  const due =
    dueAmount && dueUnit
      ? `con ${dueAmount} ${plural(dueAmount, dueUnit)} para realizarlo`
      : 'sin plazo: disponible todo el ciclo';

  return `${cycle}, ${due}.`;
};
